import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock3, Star } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80";

const LINES = [
  { text: "Votre patrimoine.", accent: false },
  { text: "Votre famille.", accent: true },
  { text: "Notre engagement.", accent: false },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 75% 30%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(ellipse 50% 40% at 10% 90%, rgba(232,170,101,0.06), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 pt-24 sm:pt-36 lg:pt-44 pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 lg:gap-12 items-center">
        <motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-amber/90 mb-8 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-10 bg-amber/60" />
            Courtier en assurance indépendant
          </motion.p>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.12] tracking-tight text-ivory" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pt-[0.15em] -mt-[0.15em] pb-[0.2em] -mb-[0.2em]">
                <motion.span
                  className={`block ${line.accent ? "italic text-amber" : ""}`}
                  initial={{ y: "130%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-ivory/60"
            data-testid="hero-subcopy"
          >
            ASSU-FRANCE négocie pour vous auprès de plus de 40 assureurs majeurs.
            Des couvertures sur-mesure, un conseiller dédié, et jusqu'à{" "}
            <span className="text-amber font-medium">28&nbsp;% d'économies</span> sur vos contrats.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 block lg:hidden"
          >
            <div className="overflow-hidden rounded-[1.4rem] border border-ivory/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
              <img
                src={HERO_IMG}
                alt="Étude de devis réussie : un conseiller ASSU-FRANCE et sa cliente"
                data-testid="hero-image-mobile"
                className="h-44 sm:h-56 w-full object-cover"
              />
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.9 }}
            className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/40"
            data-testid="hero-orias"
          >
            Courtier immatriculé à l'ORIAS sous le n° 25003318 — vérifiable sur orias.fr
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-5"
          >
            <a
              href="#devis"
              data-testid="hero-quote-cta"
              className="group inline-flex w-full sm:w-auto justify-center items-center gap-3 rounded-full bg-amber px-8 py-4 font-sans text-base font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-1"
            >
              Obtenir mon devis gratuit
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#expertises"
              data-testid="hero-secondary-cta"
              className="font-mono text-xs uppercase tracking-[0.2em] text-ivory/70 hover:text-amber border-b border-ivory/20 hover:border-amber pb-1 transition-colors duration-300"
            >
              Découvrir nos expertises
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-14 flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-4 text-ivory/50 text-center lg:text-left"
            data-testid="hero-trust-row"
          >
            <span className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-amber" /> Conseiller dédié
            </span>
            <span className="flex items-center gap-2 text-sm">
              <Clock3 className="h-4 w-4 text-amber" /> Réponse sous 24h
            </span>
            <span className="flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 text-amber" /> 98,4&nbsp;% de satisfaction
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block mt-6 lg:mt-0"
        >
          <div className="absolute -inset-8 rounded-[2rem] bg-amber/10 blur-3xl" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[1.6rem] border border-ivory/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]" style={{ clipPath: "inset(0 round 1.6rem)" }}>
            <motion.img
              src={HERO_IMG}
              alt="Étude de devis réussie : un conseiller ASSU-FRANCE et sa cliente"
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.15 }}
              className="h-[360px] sm:h-[460px] lg:h-[540px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
